/**
 * LineageViewer — 系譜フルビューア
 *
 * 自己拡張（Self-Expansion）の感動を最大化するインタラクティブなツリーUI。
 * SVGとFramer Motionを使用し、ドラッグ可能なキャンバス上に系譜を描画する。
 *
 * ゲームニクス: 光の線アニメーション、トリプルカラーのノード、未完了タスクの点滅
 * 4アクション制約: パン/ズーム、ノードタップ、参加ボタン、戻る
 */

import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from './icons/Icon';
import { motion, AnimatePresence } from 'framer-motion';
import { COPY, ROLE_LABELS, type RoleType } from '../lib/copyDictionary';
import { PulseButton } from './ui/PulseButton';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface LineageNode {
  work_id: string;
  parent_work_id: string | null;
  work_type: RoleType;
  creator_id: string;
  title: string | null;
  display_name: string;
  depth: number;
}

interface LineageViewerProps {
  nodes: LineageNode[];
  currentWorkId: string;
  onClose: () => void;
  onNodeSelect?: (workId: string) => void;
}

interface LayoutNode extends LineageNode {
  x: number;
  y: number;
}

const ROLE_COLORS: Record<RoleType, string> = {
  script: '#f59e0b',        // Amber
  illustration: '#8b5cf6',  // Violet
  voice: '#06b6a4',         // Teal
};

const NODE_WIDTH = 200;
const NODE_HEIGHT = 60;
const X_SPACING = 300;
const Y_SPACING = 100;

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const LineageViewer: FC<LineageViewerProps> = ({
  nodes,
  currentWorkId,
  onClose,
  onNodeSelect,
}) => {
  const { playSound, triggerCelebration } = useGamenics();
  const [selectedNodeId, setSelectedNodeId] = useState<string>(currentWorkId);
  const [stageStr, setStageStr] = useState<'init' | 'tracing' | 'ready'>('init');
  
  // ── Pan & Zoom State ──
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  // ── Layout Calculation ──
  const layout = useMemo<{ nodes: LayoutNode[], links: { src: LayoutNode, dst: LayoutNode }[] }>(() => {
    if (nodes.length === 0) return { nodes: [], links: [] };

    // Group by parent
    const childrenMap = new Map<string | null, LineageNode[]>();
    nodes.forEach(n => {
      const list = childrenMap.get(n.parent_work_id) || [];
      list.push(n);
      childrenMap.set(n.parent_work_id, list);
    });

    const layoutNodes: LayoutNode[] = [];
    let currentY = 0;

    // Simple DFS for Y-coordinate assignment
    const traverse = (node: LineageNode, depth: number) => {
      const children = childrenMap.get(node.work_id) || [];
      const nodeY = children.length === 0 ? currentY : currentY + (children.length - 1) * Y_SPACING / 2;
      
      layoutNodes.push({
        ...node,
        x: depth * X_SPACING,
        y: nodeY,
      });

      if (children.length === 0) {
        currentY += Y_SPACING;
      } else {
        children.forEach(child => traverse(child, depth + 1));
      }
    };

    // Find roots (nodes where parent_work_id is null or parent doesn't exist in array)
    const nodeIds = new Set(nodes.map(n => n.work_id));
    const roots = nodes.filter(n => !n.parent_work_id || !nodeIds.has(n.parent_work_id));
    
    roots.forEach(root => traverse(root, 0));

    // Create links
    const links: { src: LayoutNode, dst: LayoutNode }[] = [];
    const layoutNodeMap = new Map(layoutNodes.map(n => [n.work_id, n]));
    
    layoutNodes.forEach(n => {
      if (n.parent_work_id && layoutNodeMap.has(n.parent_work_id)) {
        links.push({
          src: layoutNodeMap.get(n.parent_work_id)!,
          dst: n,
        });
      }
    });

    return { nodes: layoutNodes, links };
  }, [nodes]);

  // ── Initial Animation Sequence ──
  useEffect(() => {
    // 500msの暗転・「間」
    const t1 = setTimeout(() => {
      playSound(GamenicsEvent.BATTLE_START); // 代用重低音
      setStageStr('tracing');
    }, 500);

    // 1500msでトレース完了、全ノード表示
    const t2 = setTimeout(() => {
      playSound(GamenicsEvent.SUBMISSION_COMPLETE); // シャラーン
      setStageStr('ready');
      triggerCelebration(GamenicsEvent.MILESTONE_10); // エフェクト
    }, 2000);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [playSound, triggerCelebration]);

  // Center on current node after layout
  useEffect(() => {
    const current = layout.nodes.find(n => n.work_id === currentWorkId);
    if (current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTransform({
        x: (rect.width / 2) - current.x,
        y: (rect.height / 2) - current.y,
        scale: 1
      });
    }
  }, [layout, currentWorkId]);

  // ── Mouse Events for Panning ──
  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    startPos.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    setTransform(prev => ({
      ...prev,
      x: e.clientX - startPos.current.x,
      y: e.clientY - startPos.current.y,
    }));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    isDragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomIntensity = 0.001;
    const newScale = Math.min(Math.max(0.3, transform.scale - e.deltaY * zoomIntensity), 2);
    setTransform(prev => ({ ...prev, scale: newScale }));
  };

  // ── Missing Roles Calculation (Zeigarnik Effect) ──
  const getMissingRolesForNode = (nodeId: string): RoleType[] => {
    const parentNode = layout.nodes.find(n => n.work_id === nodeId);
    if (!parentNode) return [];
    
    // 次の期待されるロール（台本 → 絵 → 声）
    const nextRoleMap: Record<RoleType, RoleType[]> = {
      script: ['illustration', 'voice'], // 基本は絵、直接声もアリ
      illustration: ['voice'],
      voice: [],
    };
    
    const possibleNextRoles = nextRoleMap[parentNode.work_type];
    if (possibleNextRoles.length === 0) return [];

    // 既に直下に存在する子ノードのロールを確認
    const children = layout.nodes.filter(n => n.parent_work_id === nodeId);
    const existingChildRoles = new Set(children.map(c => c.work_type));

    // まだ存在しないロールを返す
    return possibleNextRoles.filter(r => !existingChildRoles.has(r));
  };

  const selectedMissingRoles = getMissingRolesForNode(selectedNodeId);

  // ── Render ──
  return (
    <div className="fixed inset-0 z-[60] bg-cream text-ink overflow-hidden flex flex-col animate-slide-up">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-6 z-20 pointer-events-none flex justify-between items-center bg-gradient-to-b from-cream to-transparent">
        <div>
          <h2 className="text-2xl font-display font-bold flex items-center gap-2">
            カルチャー・リネージュ
          </h2>
          <p className="text-ink-2 text-sm mt-1">
            この作品を中心に広がる共創の樹
          </p>
        </div>
        <PulseButton
          variant="ghost"
          size="sm"
          className="text-ink-2 hover:text-ink px-4 py-2 pointer-events-auto transition-colors"
          onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}
        >
          <Icon name="x" size={16} /> {COPY.close}
        </PulseButton>
      </div>

      {/* Canvas Area */}
      <div 
        ref={containerRef}
        className="flex-1 w-full h-full cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onWheel={handleWheel}
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)',
          backgroundSize: `${40 * transform.scale}px ${40 * transform.scale}px`,
          backgroundPosition: `${transform.x}px ${transform.y}px`,
        }}
      >
        <div style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: '0 0',
          width: '100%', height: '100%',
        }}>
          {/* SVG Connection Lines */}
          <svg className="absolute inset-0 overflow-visible pointer-events-none">
            {/* Base lines (dimmed) */}
            {layout.links.map((link, i) => {
              const dx = link.dst.x - link.src.x;
              const path = `M ${link.src.x + NODE_WIDTH} ${link.src.y + NODE_HEIGHT/2} C ${link.src.x + NODE_WIDTH + dx/2} ${link.src.y + NODE_HEIGHT/2}, ${link.dst.x - dx/2} ${link.dst.y + NODE_HEIGHT/2}, ${link.dst.x} ${link.dst.y + NODE_HEIGHT/2}`;
              return (
                <path
                  key={`base-${i}`}
                  d={path}
                  fill="none"
                  stroke="rgba(36,28,46,0.15)"
                  strokeWidth={2}
                />
              );
            })}

            {/* Tracing lines (animated on entry) */}
            {layout.links.map((link, i) => {
              const dx = link.dst.x - link.src.x;
              const path = `M ${link.src.x + NODE_WIDTH} ${link.src.y + NODE_HEIGHT/2} C ${link.src.x + NODE_WIDTH + dx/2} ${link.src.y + NODE_HEIGHT/2}, ${link.dst.x - dx/2} ${link.dst.y + NODE_HEIGHT/2}, ${link.dst.x} ${link.dst.y + NODE_HEIGHT/2}`;
              
              // Only draw if we are tracing or ready
              if (stageStr === 'init') return null;
              
              return (
                <motion.path
                  key={`trace-${i}`}
                  d={path}
                  fill="none"
                  stroke={ROLE_COLORS[link.dst.work_type]}
                  strokeWidth={3}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ 
                    pathLength: stageStr === 'ready' ? 1 : 1,
                    opacity: stageStr === 'ready' ? 0.6 : 1
                  }}
                  transition={{ 
                    duration: 1.2, 
                    ease: "easeInOut",
                    delay: stageStr === 'tracing' ? link.src.depth * 0.2 : 0 
                  }}
                  style={{
                    filter: 'drop-shadow(0 0 4px currentColor)'
                  }}
                />
              );
            })}

            {/* 未完了タスクの点線（選択ノードの直下のみ表示） */}
            {selectedMissingRoles.map((role, i) => {
              const srcNode = layout.nodes.find(n => n.work_id === selectedNodeId);
              if (!srcNode) return null;
              
              const existingChildren = layout.nodes.filter(n => n.parent_work_id === selectedNodeId);
              // 既存の子ノードがあればその下、無ければそのままのY座標ベース
              const baseOffsetY = existingChildren.length > 0 
                ? Math.max(...existingChildren.map(c => c.y)) + Y_SPACING 
                : srcNode.y;
              
              const dstX = srcNode.x + X_SPACING;
              const dstY = baseOffsetY + (i * Y_SPACING);
              const dx = dstX - srcNode.x;
              const path = `M ${srcNode.x + NODE_WIDTH} ${srcNode.y + NODE_HEIGHT/2} C ${srcNode.x + NODE_WIDTH + dx/2} ${srcNode.y + NODE_HEIGHT/2}, ${dstX - dx/2} ${dstY + NODE_HEIGHT/2}, ${dstX} ${dstY + NODE_HEIGHT/2}`;
              
              return (
                <path
                  key={`missing-line-${role}`}
                  d={path}
                  fill="none"
                  stroke={ROLE_COLORS[role]}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  className="opacity-50 animate-[pulse-fast_2s_linear_infinite]"
                />
              );
            })}
          </svg>

          {/* HTML Nodes */}
          {layout.nodes.map((node) => {
            const isVisible = stageStr === 'ready';
            const isSelected = node.work_id === selectedNodeId;
            const roleColor = ROLE_COLORS[node.work_type];
            
            return (
              <motion.div
                key={node.work_id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ 
                  opacity: isVisible ? 1 : 0, 
                  scale: isVisible ? (isSelected ? 1.05 : 1) : 0.8,
                  boxShadow: isSelected 
                    ? `0 0 20px ${roleColor}40, inset 0 0 0 2px ${roleColor}` 
                    : `0 0 0px ${roleColor}00, inset 0 0 0 1px rgba(255,255,255,0.1)`
                }}
                transition={{ 
                  duration: 0.4, 
                  delay: stageStr === 'ready' ? 0 : node.depth * 0.1 
                }}
                className="absolute bg-dark-surface rounded-xl overflow-hidden cursor-pointer backdrop-blur-md"
                style={{
                  left: node.x,
                  top: node.y,
                  width: NODE_WIDTH,
                  height: NODE_HEIGHT,
                  zIndex: isSelected ? 10 : 1
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  playSound(GamenicsEvent.TAP_CONFIRM);
                  setSelectedNodeId(node.work_id);
                  if (onNodeSelect && node.work_id !== currentWorkId) {
                    onNodeSelect(node.work_id);
                  }
                }}
              >
                {/* Node inner */}
                <div className="flex items-center p-3 gap-3 h-full">
                  <div className="w-10 h-10 rounded-full bg-cream-2 flex-shrink-0 flex items-center justify-center text-sm" style={{ color: roleColor }}>
                    {ROLE_LABELS[node.work_type].charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold mb-0.5" style={{ color: roleColor }}>
                      {ROLE_LABELS[node.work_type]}
                    </div>
                    <div className="text-ink text-xs truncate">
                      {node.display_name}
                    </div>
                  </div>
                </div>

                {/* Pulse ring for current user's node */}
                {node.work_id === currentWorkId && stageStr === 'ready' && (
                  <div className="absolute inset-0 border-2 rounded-xl border-white animate-marker-pulse pointer-events-none" />
                )}
              </motion.div>
            );
          })}

          {/* 未完了ノードのプレースホルダー */}
          <AnimatePresence>
            {selectedMissingRoles.map((role, i) => {
              const srcNode = layout.nodes.find(n => n.work_id === selectedNodeId);
              if (!srcNode) return null;
              
              const existingChildren = layout.nodes.filter(n => n.parent_work_id === selectedNodeId);
              const baseOffsetY = existingChildren.length > 0 
                ? Math.max(...existingChildren.map(c => c.y)) + Y_SPACING 
                : srcNode.y;
              
              const dstX = srcNode.x + X_SPACING;
              const dstY = baseOffsetY + (i * Y_SPACING);
              const roleColor = ROLE_COLORS[role];

              return (
                <motion.div
                  key={`missing-node-${role}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute rounded-xl border-2 border-dashed flex items-center p-3 gap-3 backdrop-blur-sm cursor-pointer hover:bg-cream-2 transition-colors"
                  style={{
                    left: dstX,
                    top: dstY,
                    width: NODE_WIDTH,
                    height: NODE_HEIGHT,
                    borderColor: `${roleColor}80`,
                    zIndex: 5
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    playSound(GamenicsEvent.TAP_CONFIRM);
                    // ここで録音画面へ遷移などの処理
                  }}
                >
                  <div className="w-10 h-10 rounded-full border border-dashed flex items-center justify-center text-xl" style={{ borderColor: roleColor, color: roleColor }}>
                    +
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink-2">
                      あなたが最初の<br/><span style={{ color: roleColor }} className="font-bold">{ROLE_LABELS[role]}</span> になる
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20 pointer-events-none bg-gradient-to-t from-cream to-transparent flex justify-center">
        {selectedNodeId !== currentWorkId && (
          <PulseButton
            isPrimary={true}
            className="pointer-events-auto"
            onClick={() => {
              playSound(GamenicsEvent.TAP_CONFIRM);
              // 作品詳細へ飛ぶなどのアクション
              onClose();
            }}
          >
            この作品を聴く
          </PulseButton>
        )}
      </div>
    </div>
  );
};
